// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========

import { Button } from '@/components/ui/button';
import { MorphIcon } from '@/components/ui/morph-icon';
import { cn } from '@/lib/utils';
import { ArrowRight, CircleHelp, Gamepad2, Joystick, X } from 'lucide';
import { useEffect, useRef, useState } from 'react';

/** Read-only agent-mode chip — mirrors the desktop `ProjectModeToggle` read-only state. */
function AgentModeChip({ mode }: { mode: string }) {
  const isSingle = mode === 'single-agent';
  const label = isSingle ? 'Single Agent' : 'Workforce';
  return (
    <div
      role="status"
      aria-label={`Agent mode: ${label}`}
      className="pointer-events-none inline-flex items-center gap-1.5 px-1 text-ds-text-neutral-muted-default"
    >
      <MorphIcon
        icon={isSingle ? Joystick : Gamepad2}
        className="h-3.5 w-3.5 shrink-0"
        strokeWidth={2}
      />
      <span className="whitespace-nowrap !text-label-xs font-semibold">
        {label}
      </span>
    </div>
  );
}

interface RemoteInputBoxProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  placeholder?: string;
  disabled?: boolean;
  /** Agent mode of the active project (read-only display). */
  agentMode?: string | null;
  /** Human-input interaction currently targeted by the composer. */
  replyingTo?: string | null;
  onCancelReply?: () => void;
}

export function RemoteInputBox({
  value,
  onChange,
  onSend,
  placeholder = 'Send a message…',
  disabled = false,
  agentMode,
  replyingTo,
  onCancelReply,
}: RemoteInputBoxProps) {
  const [isFocused, setIsFocused] = useState(false);
  const [isComposing, setIsComposing] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const hasContent = value.trim().length > 0;

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  }, [value]);

  useEffect(() => {
    if (replyingTo) textareaRef.current?.focus();
  }, [replyingTo]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !disabled && !isComposing) {
      e.preventDefault();
      if (hasContent) onSend();
    }
  };

  return (
    <div
      className={cn(
        'relative flex w-full flex-col rounded-3xl border border-solid border-ds-border-neutral-default-default bg-ds-bg-neutral-subtle-default p-3 shadow-lg transition-colors',
        isFocused && 'border-ds-border-information-default-default'
      )}
    >
      {replyingTo && (
        <div
          className="mb-2 flex min-w-0 items-center gap-2 rounded-xl bg-ds-bg-status-pending-subtle-default px-2.5 py-2 text-label-xs text-ds-text-neutral-default-default"
          role="status"
        >
          <MorphIcon
            icon={CircleHelp}
            className="h-3.5 w-3.5 shrink-0 text-ds-icon-neutral-muted-default"
          />
          <span className="min-w-0 flex-1 truncate">
            Replying to: {replyingTo}
          </span>
          {onCancelReply && (
            <button
              type="button"
              className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full hover:bg-ds-bg-neutral-subtle-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-ring-brand-default-focus"
              aria-label="Cancel question reply"
              onClick={onCancelReply}
            >
              <MorphIcon icon={X} className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      )}
      {/* Text input */}
      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onCompositionStart={() => setIsComposing(true)}
        onCompositionEnd={() => setIsComposing(false)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        placeholder={placeholder}
        disabled={disabled}
        rows={1}
        className={cn(
          // Match the conversation text size exactly (messages render at 13px).
          // Pinned with an explicit px value because the body-sm token leaves the
          // browser's default form-control size (13.33px) showing on <textarea>.
          // The iOS focus auto-zoom this size would trigger is suppressed via the
          // viewport's maximum-scale in index.html.
          'w-full resize-none bg-transparent pb-3 font-sans text-[13px] leading-[20px] text-ds-text-neutral-default-default outline-none',
          'placeholder:font-sans placeholder:text-ds-text-neutral-subtle-disabled',
          'max-h-[200px] min-h-[40px]',
          'disabled:cursor-not-allowed disabled:opacity-60',
          'border-none shadow-none focus-visible:ring-0'
        )}
      />

      {/* Action row */}
      <div className="flex w-full items-center justify-end">
        {/* Right: agent mode chip + send button */}
        <div className="flex items-center gap-2">
          {agentMode ? <AgentModeChip mode={agentMode} /> : null}
          <Button
            type="button"
            size="xs"
            buttonContent="icon-only"
            buttonRadius="full"
            variant="primary"
            tone={hasContent ? 'success' : 'neutral'}
            onClick={onSend}
            disabled={disabled || !hasContent}
            aria-label="Send"
          >
            <MorphIcon
              icon={ArrowRight}
              className={cn(
                'text-current transition-transform duration-200',
                hasContent && '-rotate-90'
              )}
            />
          </Button>
        </div>
      </div>
    </div>
  );
}
